import React, { useEffect, useState } from "react";
import { INewNotice, INotice } from "../../exports/interfaces";
import { useNavigate } from "react-router-dom";
import {
  customSelectStyles,
  SelectOption,
} from "../../exports/customSelectStyles";
import Select from "react-select";
import { Upload } from "lucide-react";
import useMobile from "../../hooks/useMobile";
import { useLoader } from "../../context/LoaderContext";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { useOption } from "../../context/OptionContext";
import {
  renderExistingFilePreview,
  renderFilePreview,
  checkNullDate,
} from "../../exports/functions";

const SelectWithError = Select as unknown as React.ComponentType<any>;

const EditNotice = ({ notice }: { notice: INotice | undefined }) => {
  const isMobile = useMobile();
  const { categories } = useOption();
  const { setLoading } = useLoader();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<INotice | null>(null);
  const [errors, setErrors] = useState({
    noticeType: "",
    noticeTo: "",
    cooperativeName: "",
    noticeTitle: "",
    publishDate: "",
    endDate: "",
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setFormData((prev) =>
      prev
        ? {
            ...prev,
            fileName: file.name,
            file,
          }
        : prev,
    );
  };

  useEffect(() => {
    if (notice) {
      setFormData({
        ...notice,
        publishDate: checkNullDate(notice.publishDate),
        endDate: checkNullDate(notice.endDate),
      });
    }
  }, [notice]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) =>
      prev
        ? {
            ...prev,
            [name]: value,
          }
        : prev,
    );
    // setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const getNoticeErrors = (data: INotice | null) => {
    const validationErrors = {
      noticeType: "",
      noticeTo: "",
      cooperativeName: "",
      noticeTitle: "",
      publishDate: "",
      endDate: "",
    };

    if (!data?.noticeType?.trim()) {
      validationErrors.noticeType = "Notice Type is required.";
    }

    if (!data?.noticeTo?.trim()) {
      validationErrors.noticeTo = "Notice To is required.";
    }

    if (!data?.cooperativeName?.trim()) {
      validationErrors.cooperativeName = "Cooperative Name is required.";
    }

    if (!data?.noticeTitle?.trim()) {
      validationErrors.noticeTitle = "Notice Title is required.";
    }

    if (!data?.publishDate?.trim()) {
      validationErrors.publishDate = "Notice Publish Date is required.";
    }

    // if (!data?.endDate?.trim()) {
    //   validationErrors.endDate = "Notice End Date is required.";
    // }

    return validationErrors;
  };

  const scrollToFirstError = (validationErrors: Record<string, string>) => {
    const fieldOrder = [
      "noticeType",
      "noticeTo",
      "cooperativeName",
      "noticeTitle",
      "publishDate",
      "endDate",
    ];

    const firstErrorField = fieldOrder.find((field) => validationErrors[field]);

    if (!firstErrorField) return;

    const element =
      firstErrorField === "noticeType" || firstErrorField === "noticeTo"
        ? document.querySelector(`[data-field="${firstErrorField}"]`) as HTMLElement | null
        : document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement | null;

    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      // element.focus?.();
    }
  };

  const validateNoticeFields = (data: INotice | null) => {
    const validationErrors = getNoticeErrors(data);
    setErrors(validationErrors);

    if (Object.values(validationErrors).some((error) => error !== "")) {
      scrollToFirstError(validationErrors);
      return false;
    }

    return true;
  };

  const noticeTypeOptions =
    categories
      ?.find((item) => item.name === "Notice Type")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const noticeToOptions =
    categories
      ?.find((item) => item.name === "Notice Audience")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const handleSelectChange = (
    name: keyof INewNotice,
    selected: SelectOption | null,
  ) => {
    setFormData((prev) =>
      prev
        ? {
            ...prev,
            [name]: selected?.value,
          }
        : prev,
    );
    // setErrors((prev) => ({ ...prev, [name]: "" }));
  };
  const handleSubmit = async () => {
    if (!formData) return;

    if (!validateNoticeFields(formData)) {
      return;
    }

    setLoading(true);

    try {
      const payload = new FormData();

      payload.append("noticeType", formData.noticeType || "");
      payload.append("noticeTo", formData.noticeTo || "");
      payload.append("cooperativeName", formData.cooperativeName || "");
      payload.append("noticeTitle", formData.noticeTitle || "");
      payload.append("noticeDetails", formData.noticeDetails || "");
      payload.append("publishDate", formData.publishDate || "");
      payload.append("endDate", formData.endDate || "");

      if (formData.file) {
        payload.append("documentUrl", formData.file);
      }

      const response = await axiosClient.put(
        `/notices/${formData.id}`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.status === 200 || response.status === 201) {
        dynamicSuccess("Notice updated successfully!");
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Something went wrong";
      dynamicFailed(message);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };
  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3>Edit Notice</h3>
          <small>
            Record a new Notice from a cooperative member or stakeholder
          </small>
        </div>
      </div>

      <div className="dashboard-card p-3 mt-3">
        <h5 className="text-uppercase mb-4">Notice Details</h5>

        <div className="row g-3">
          <div className="col-md-4" data-field="noticeType">
            <label className="form-label">
              Notice Type <span className="primary-color">*</span>
            </label>
            <SelectWithError
              styles={customSelectStyles}
              isError={Boolean(errors.noticeType)}
              options={noticeTypeOptions}
              placeholder="Select Notice Type"
              value={
                noticeTypeOptions.find(
                  (option) => option.value === formData?.noticeType,
                ) || null
              }
              onChange={(selected: SelectOption | null) =>
                handleSelectChange("noticeType", selected)
              }
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {errors.noticeType && (
              <p className="error-message">{errors.noticeType}</p>
            )}
          </div>
          <div className="col-md-4" data-field="noticeTo">
            <label className="form-label">
              Notice To <span className="primary-color">*</span>
            </label>
            <SelectWithError
              styles={customSelectStyles}
              isError={Boolean(errors.noticeTo)}
              options={noticeToOptions}
              placeholder="Select Notice To"
              value={
                noticeToOptions.find(
                  (option) => option.value === formData?.noticeTo,
                ) || null
              }
              onChange={(selected: SelectOption | null) => handleSelectChange("noticeTo", selected)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {errors.noticeTo && (
              <p className="error-message">{errors.noticeTo}</p>
            )}
          </div>
          {!isMobile && <div className="col-md-4"></div>}
          <div className="col-md-8">
            <label className="form-label">
              {" "}
              Cooperative Name <span className="primary-color">*</span>
            </label>
            <input
              className={`form-control ${errors.cooperativeName ? "input-error" : ""}`}
              type="text"
              name="cooperativeName"
              value={formData?.cooperativeName}
              onChange={handleChange}
              placeholder="Enter Cooperative Name"
            />
            {errors.cooperativeName && (
              <p className="error-message">{errors.cooperativeName}</p>
            )}
          </div>

          {!isMobile && <div className="col-md-4"></div>}
          <div className="col-md-8">
            <label className="form-label">
              {" "}
              Notice Title <span className="primary-color">*</span>
            </label>
            <input
              className={`form-control ${errors.noticeTitle ? "input-error" : ""}`}
              type="text"
              name="noticeTitle"
              value={formData?.noticeTitle}
              onChange={handleChange}
              placeholder="Enter Notice Title"
            />
            {errors.noticeTitle && (
              <p className="error-message">{errors.noticeTitle}</p>
            )}
          </div>
          <div className="col-12">
            <label className="form-label">Notice Details</label>
            <textarea
              className="form-control"
              rows={5}
              name="noticeDetails"
              value={formData?.noticeDetails}
              onChange={handleChange}
              placeholder="Enter Notice Details"
            />
          </div>
          <div className="col-12">
            <label className="form-label">
              Notice Document (PDF, JPEG, PNG)
            </label>

            <div
              className={`upload-box p-4 ${!formData?.file && !formData?.documentUrl && "text-center"}`}
            >
              {formData?.file ? (
                renderFilePreview(formData.file, formData.fileName, (e: any) =>
                  handleFileChange(e),
                )
              ) : formData?.documentUrl ? (
                renderExistingFilePreview(
                  formData.documentUrl,
                  handleFileChange,
                )
              ) : (
                <>
                  <Upload />

                  <p className="mb-1">
                    Choose a file or drag and drop it here.
                  </p>

                  <small className="d-block mb-3">
                    (txt, docx, xlsx, pdf, jpeg, png - Up to 100 mb)
                  </small>

                  <label className="btn btn-badge text-main-color">
                    Browse File
                    <input
                      hidden
                      type="file"
                      onChange={(e) => {
                        handleFileChange(e);
                      }}
                    />
                  </label>
                </>
              )}
            </div>
          </div>
          <div className="col-md-3">
            <label className="form-label">
              Notice Publish Date <span className="primary-color">*</span>
            </label>
            <input
              type="date"
              className={`form-control ${errors.publishDate ? "input-error" : ""}`}
              name="publishDate"
              value={formData?.publishDate}
              onChange={handleChange}
            />
            {errors.publishDate && (
              <p className="error-message">{errors.publishDate}</p>
            )}
          </div>
          {!isMobile && (
            <>
              <div className="col-md-3"></div> <div className="col-md-3"></div>
            </>
          )}

          <div className="col-md-3">
            <label className="form-label">
              Notice End Date
            </label>
            <input
              type="date"
              className={`form-control ${errors.endDate ? "input-error" : ""}`}
              name="endDate"
              value={formData?.endDate}
              onChange={handleChange}
            />
            {errors.endDate && (
              <p className="error-message">{errors.endDate}</p>
            )}
          </div>
        </div>

        <div className="text-end mt-3">
          <button
            className="btn btn-badge-border px-3 me-2"
            onClick={() => navigate("/complaints-list")}
          >
            Cancel
          </button>
          <button className="btn btn-badge-primary px-3" onClick={handleSubmit}>
            Update
          </button>
        </div>
      </div>

      {/* <div className="text-end mt-3">
           <button
             className="btn btn-badge-primary px-3"
             onClick={() => navigate("/complaints-list")}
           >
             Back to list
           </button>
         </div> */}
    </>
  );
};

export default EditNotice;
